import React, { useRef } from 'react';
import { Box, Button, Typography, CircularProgress, Link } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { brandColors, RADIUS, BASE_FONT_SIZE } from '../../theme/appTheme';

interface FileUploadFieldProps {
  label: string;
  value?: string;
  onUpload: (file: File) => Promise<string>;
  onChange?: (url: string) => void;
  helperText?: string;
  accept?: string;
  maxSizeMB?: number;
  required?: boolean;
}

const isImageUrl = (url: string) => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  label,
  value,
  onUpload,
  onChange,
  helperText,
  accept = 'application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png',
  maxSizeMB = 2,
  required,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setError('');
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File too large. Max ${maxSizeMB}MB`);
      return;
    }
    setUploading(true);
    try {
      const url = await onUpload(file);
      onChange?.(url);
    } catch {
      setError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 600, mb: 1, color: brandColors.textDark }}>
        {label}{required ? ' *' : ''}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
          p: 1.5,
          borderRadius: `${RADIUS}px`,
          border: `1px dashed ${brandColors.border}`,
          bgcolor: '#fff',
        }}
      >
        {value ? (
          isImageUrl(value) ? (
            <Box
              component="img"
              src={value}
              alt={label}
              sx={{
                maxHeight: 64,
                maxWidth: 120,
                objectFit: 'contain',
                border: `1px solid ${brandColors.border}`,
                borderRadius: 1,
                p: 0.5,
                bgcolor: '#fff',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <InsertDriveFileOutlinedIcon sx={{ color: brandColors.textMuted, fontSize: 22 }} />
              <Link href={value} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, fontWeight: 600 }}>
                View document
              </Link>
            </Box>
          )
        ) : (
          <Typography sx={{ fontSize: 13, color: brandColors.textMuted }}>No file chosen</Typography>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px`, ml: 'auto' }}
        >
          {value ? 'Replace file' : 'Choose file'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </Box>
      <Typography sx={{ fontSize: 11, color: brandColors.textMuted, mt: 0.5 }}>
        {helperText || `Upload PDF or Image (Max ${maxSizeMB}MB)`}
      </Typography>
      {error && (
        <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>{error}</Typography>
      )}
    </Box>
  );
};
